import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { submitContactForm } from '@/services/contactService';
import { useContactForm } from '@/hooks/useContactForm';
import { useToast } from '@/components/ui/use-toast';

export default function Contact() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSuccess, setIsSuccess] = useState(false);
  
  const {
    state,
    updateField,
    isFormValid,
    resetForm
  } = useContactForm();

  const { data: formData, errors } = state;
  const isValid = isFormValid();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValid) {
      toast({
        title: 'Please fill in all required fields',
        description: 'Make sure all fields are filled correctly.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await submitContactForm(formData);
      if (response.success) {
        setIsSuccess(true);
        toast({
          title: 'Message sent successfully!',
          description: 'Thanks for reaching out! We\'ll get back to you soon.',
          variant: 'default'
        });
      } else {
        throw new Error(response.error || 'Failed to send message');
      }
    } catch (error) {
      toast({
        title: 'Failed to send message',
        description: 'Please check your information and try again.',
        variant: 'destructive'
      });
    }
  };

  const handleSendNewEmail = () => {
    setIsSuccess(false);
    resetForm();
  };

  const handleGoHome = () => {
    navigate('/home');
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#160C0C] flex flex-col">
        <TopNavigation />
        <main className="flex-1 flex flex-col items-center justify-center pb-24 pt-20">
          <div className="w-full max-w-md px-6 py-8 text-center">
            {/* Success Messages */}
            <h1 className="text-2xl font-bold text-white mb-4">
              Your message has been sent.
            </h1>
            <p className="text-gray-300 mb-8">
              Thanks for reaching out! We've received your message and will get back to you soon.
            </p>
            
            {/* Action Buttons */}
            <div className="flex flex-col space-y-4">
              <button
                onClick={handleSendNewEmail}
                className="w-full px-6 py-4 bg-[#D72E16] text-white rounded-full font-semibold hover:bg-[#B91C1C] transition"
              >
                Send new email
              </button>
              <button
                onClick={handleGoHome}
                className="w-full px-6 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition"
              >
                Go Home
              </button>
            </div>
          </div>
        </main>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#160C0C] flex flex-col">
      <TopNavigation />
      <main className="flex-1 flex flex-col items-center justify-center pb-24 pt-20">
        <div className="w-full max-w-md px-6 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-gray-300 hover:text-white mb-6 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>

          <form onSubmit={handleSubmit} className="w-full space-y-6">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">Contact Us</h1>
              <p className="text-gray-300">
                Have a question or a suggestion? We're on the mat to help.
              </p>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D72E16] focus:border-transparent"
                  placeholder="Enter your name"
                />
                {errors.name && (
                  <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Your Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D72E16] focus:border-transparent"
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Subject Field */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => updateField('subject', e.target.value)}
                  maxLength={60}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D72E16] focus:border-transparent"
                  placeholder="What's this about?"
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.subject && (
                    <p className="text-red-400 text-sm">{errors.subject}</p>
                  )}
                  <p className="text-gray-400 text-sm ml-auto">
                    {formData.subject.length}/60
                  </p>
                </div>
              </div>

              {/* Description Field */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  maxLength={2000}
                  rows={6}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D72E16] focus:border-transparent resize-none"
                  placeholder="Tell us more about your question or suggestion..."
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.description && (
                    <p className="text-red-400 text-sm">{errors.description}</p>
                  )}
                  <p className="text-gray-400 text-sm ml-auto">
                    {formData.description.length}/2000
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={!isValid}
                className="w-full px-6 py-4 bg-[#D72E16] text-white rounded-full font-semibold hover:bg-[#B91C1C] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Message
              </button>
            </div>
          </form>
        </div>
      </main>
      <BottomNavigation />
    </div>
  );
}
